
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Filter, Company } from '../../types';
import { firestoreService } from '../../services/firestoreService';
import Spinner from '../../components/common/Spinner';
import AddFilterModal from '../../components/admin/AddFilterModal';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { AuthContext } from '../../contexts/AuthContext';

const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L16.732 3.732z" /></svg>;
const DeleteIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;

const ManageCompanyFiltersPage: React.FC = () => {
    const { companyId } = useParams<{ companyId: string }>();
    const [company, setCompany] = useState<Company | null>(null);
    const [filters, setFilters] = useState<Filter[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingFilter, setEditingFilter] = useState<Filter | null>(null);
    const [filterToDelete, setFilterToDelete] = useState<Filter | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const authContext = useContext(AuthContext);
    const tenantId = authContext?.user?.tenantId;

    const fetchData = useCallback(async () => {
        if (!companyId || !tenantId) return;
        setLoading(true);
        try {
            // Fixed: Added tenantId to getFilters call
            const [fetchedCompany, fetchedFilters] = await Promise.all([
                firestoreService.getCompany(companyId),
                firestoreService.getFilters(tenantId, companyId),
            ]);
            setCompany(fetchedCompany || null);
            setFilters(fetchedFilters);
        } catch (error) {
            console.error("Failed to fetch company data", error);
        } finally {
            setLoading(false);
        }
    }, [companyId, tenantId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleOpenModal = (filter: Filter | null) => {
        setEditingFilter(filter);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setEditingFilter(null);
        setIsModalOpen(false);
    };

    const handleSaveFilter = async (filterData: Omit<Filter, 'filterId' | 'companyId' | 'createdAt'> | Filter) => {
        if (!companyId || !tenantId) return;
        try {
            if ('filterId' in filterData) { // Editing
                // Fixed: Called updateFilter
                await firestoreService.updateFilter(filterData.filterId, filterData);
            } else { // Adding
                // Fixed: Called addFilter and included tenantId
                await firestoreService.addFilter({ ...filterData, companyId, tenantId });
            }
            handleCloseModal();
            fetchData(); // Refresh data
        } catch (error) {
            console.error("Failed to save filter", error);
            alert(`Error saving filter: ${(error as Error).message}`);
            throw error; // Re-throw to be caught by the modal's handler
        }
    };

    const handleDeleteClick = (filter: Filter) => {
        setFilterToDelete(filter);
    };

    const confirmDeleteFilter = async () => {
        if (!filterToDelete) return;
        setIsDeleting(true);
        try {
            // Fixed: Called deleteFilter
            await firestoreService.deleteFilter(filterToDelete.filterId);
            fetchData(); // Refresh data
            setFilterToDelete(null);
        } catch (error) {
            console.error("Failed to delete filter:", error);
            alert(`There was an error deleting the filter: ${(error as Error).message}`);
        } finally {
            setIsDeleting(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center items-center h-full"><Spinner size="lg" /></div>;
    }

    if (!company) {
        return <div className="text-center text-red-400">Company not found.</div>;
    }

    return (
        <div className="container mx-auto">
            <Link to="/filters" className="text-blue-400 hover:text-blue-300 mb-6 inline-flex items-center gap-2">
                 <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                 Back to Company List
            </Link>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-white">Filters for {company.name}</h1>
                <button
                    onClick={() => handleOpenModal(null)}
                    className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                    Add Filter
                </button>
            </div>

            <div className="bg-gray-900 rounded-lg shadow-lg overflow-hidden">
                <table className="min-w-full">
                    <thead className="bg-gray-800/50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Options</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                        {filters.map((filter) => (
                            <tr key={filter.filterId} className="hover:bg-gray-800/40 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">{filter.name}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 capitalize">{filter.type}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 truncate max-w-xs">{filter.options?.join(', ') || 'N/A'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                    <button onClick={() => handleOpenModal(filter)} className="p-2 rounded-md bg-gray-700 hover:bg-blue-600 transition-colors" title="Edit Filter"><EditIcon /></button>
                                    <button onClick={() => handleDeleteClick(filter)} className="p-2 rounded-md bg-gray-700 hover:bg-red-600 transition-colors" title="Delete Filter"><DeleteIcon /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                 {filters.length === 0 && <p className="text-center text-gray-500 py-8">No filters found for this company.</p>}
            </div>

            {isModalOpen && (
                <AddFilterModal
                    onClose={handleCloseModal}
                    onSave={handleSaveFilter}
                    filterToEdit={editingFilter}
                />
            )}
            <ConfirmationModal
                isOpen={!!filterToDelete}
                onClose={() => setFilterToDelete(null)}
                onConfirm={confirmDeleteFilter}
                title="Delete Filter"
                message={<>Are you sure you want to delete the filter <strong>{filterToDelete?.name}</strong>? This action cannot be undone.</>}
                isConfirming={isDeleting}
            />
        </div>
    );
};

export default ManageCompanyFiltersPage;
